"use client";
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

interface LinkedAppointmentsContextValue {
    /** Set of appointment IDs currently trembling */
    tremblingIds: Set<string>;
    /** Start trembling for the given appointment IDs */
    startTrembling: (ids: string[]) => void;
    /** Stop all trembling */
    stopTrembling: () => void;
    /** Call before toggling to skip auto-dismiss */
    skipNextDismiss: () => void;
}

const LinkedAppointmentsContext = createContext<LinkedAppointmentsContextValue>({
    tremblingIds: new Set(),
    startTrembling: () => { },
    stopTrembling: () => { },
    skipNextDismiss: () => { },
});

export function useLinkedAppointments() {
    return useContext(LinkedAppointmentsContext);
}

export function LinkedAppointmentsProvider({ children }: { children: React.ReactNode }) {
    const [tremblingIds, setTremblingIds] = useState<Set<string>>(new Set());
    const skipDismissRef = useRef(false);

    const startTrembling = useCallback((ids: string[]) => {
        setTremblingIds(new Set(ids));
    }, []);

    const stopTrembling = useCallback(() => {
        setTremblingIds(new Set());
    }, []);

    const skipNextDismiss = useCallback(() => {
        skipDismissRef.current = true;
    }, []);

    // Listen for clicks anywhere to dismiss trembling
    useEffect(() => {
        if (tremblingIds.size === 0) return;

        const handleClick = () => {
            if (skipDismissRef.current) {
                skipDismissRef.current = false;
                return;
            }
            stopTrembling();
        };

        // Use capture phase so we catch clicks before they're stopped by e.stopPropagation
        document.addEventListener("click", handleClick, { capture: true });
        return () => {
            document.removeEventListener("click", handleClick, { capture: true });
        };
    }, [tremblingIds.size, stopTrembling]);

    return (
        <LinkedAppointmentsContext.Provider value={{ tremblingIds, startTrembling, stopTrembling, skipNextDismiss }}>
            {children}
        </LinkedAppointmentsContext.Provider>
    );
}
